/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { deleteApi, getApi, postApi } from 'redux/apis';
import {
	getCompanyDetails,
	updateCompanyConnect,
	updateCompanyStatus,
} from 'redux/slice/companySlice';
import { AppDispatch } from 'redux/store';
import { toastText } from 'utils/utils';
import DisconnectModal from './DisconnectModal';
import WarningModal from './BuySubscriptionModal';

/**
 * Shared company integration actions (Disconnect / Cancel Subscription /
 * Reconnect) plus their confirmation modals. Extracted verbatim from the old
 * ActiveConnectionsTable so both the table and the per-company Manage page use
 * the exact same, already-working Outseta flow.
 *
 * Returns trigger functions, the `modals` element to render once, and loading.
 */
export const useCompanyActions = (opts: {
	setSelectedSidebar: (v: string) => void;
	closeDrawerByAnimation: (v: boolean) => void;
}) => {
	const { setSelectedSidebar, closeDrawerByAnimation } = opts;

	const dispatch = useDispatch<AppDispatch>();

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
	const [isModalVisible, setModalVisible] = useState(false);

	const [companyId, setCompanyId] = useState('');
	const [isConnected, setIsConnected] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [modalAction, setModalAction] = useState('');

	const { data: companiesData } = useSelector((state: any) => state?.companies);

	const showModal = (connected: any, data: any, action: string) => {
		setModalAction(action);
		setCompanyId(data?.key);
		setIsConnected(connected);
		setIsModalOpen(true);
	};

	const hideModal = () => {
		setCompanyId('');
		setIsConnected(false);
		setIsModalOpen(false);
		setModalAction('');
	};

	const showConnectModal = (connected: any, data: any) => {
		setCompanyId(data?.key);
		setIsConnected(connected);
		setIsConnectModalOpen(true);
	};

	const hideConnectModal = () => {
		setCompanyId('');
		setIsConnected(false);
		setIsConnectModalOpen(false);
		setModalAction('');
	};

	const submitHandler = () => {
		if (isConnected == true && modalAction === 'Disconnect') {
			setIsLoading(true);

			const company = companiesData?.find(
				(c: any) => c?.companyId === companyId || c?.company?.id === companyId
			);

			const subscriptions = company?.company?.Subscription;
			const subscription =
				subscriptions && Array.isArray(subscriptions) ? subscriptions[0] : null;
			const newPlanUid = process.env.REACT_APP_OUTSETA_PLAN_UID_2;

			let isNewOutsetaPlan = false;
			let outSetaAccountUid: string | null = null;

			if (subscription && newPlanUid) {
				const zohoProductId =
					subscription?.zohoProductId ||
					subscription?.zohoSubscriptionPlan?.Plan.Uid;

				if (zohoProductId && zohoProductId === newPlanUid) {
					isNewOutsetaPlan = true;
				} else {
					isNewOutsetaPlan = false;
				}

				outSetaAccountUid =
					subscription?.zohoSubscriptionPlan?.Account?.Uid || null;

				if (!outSetaAccountUid) {
					console.error('No account UID found');
					return;
				}
			} else {
				console.error('No subscription found');
				return;
			}

			const disconnectPayload: any = {
				companyId: companyId,
				outSetaToken: localStorage.getItem('outsetaAccessToken'),
				isNewOutsetaPlan: isNewOutsetaPlan,
				outSetaAccountUid: outSetaAccountUid,
			};

			postApi('/quickbooks/disconnect', disconnectPayload)
				.then((res: any) => {
					setIsModalOpen(false);
					setModalAction('');

					dispatch(
						updateCompanyConnect({
							companyId: companyId,
							value: false,
						})
					);
					setIsLoading(false);
					toastText(res?.data?.message, 'success');
					localStorage.removeItem('companyId');
					window.location.reload();
				})
				.then(() => {
					const params = {
						data: {
							key: companyId,
						},
						value: false,
					};

					dispatch(updateCompanyStatus(params));
				})
				.then(() => {
					setSelectedSidebar('Integrations');
				})
				.catch((err: any) => {
					setIsModalOpen(false);
					setModalAction('');

					setIsLoading(false);
					toastText(err?.response?.data?.message, 'error');
				});
		} else if (isConnected && modalAction === 'Cancel Subscription') {
			setIsLoading(true);

			const company = companiesData?.find(
				(c: any) => c?.companyId === companyId || c?.company?.id === companyId
			);

			const subscriptions = company?.company?.Subscription;
			const subscription =
				subscriptions && Array.isArray(subscriptions) ? subscriptions[0] : null;
			const newPlanUid = process.env.REACT_APP_OUTSETA_PLAN_UID_2;

			let isNewOutsetaPlan = false;
			let outSetaAccountUid: string | null = null;

			if (subscription && newPlanUid) {
				const zohoProductId =
					subscription?.zohoProductId ||
					subscription?.zohoSubscriptionPlan?.Plan?.Uid;

				if (zohoProductId && zohoProductId === newPlanUid) {
					isNewOutsetaPlan = true;
				} else {
					isNewOutsetaPlan = false;
				}

				outSetaAccountUid =
					subscription?.zohoSubscriptionPlan?.Account?.Uid || null;

				if (!outSetaAccountUid) {
					console.error('No account UID found');
					setIsLoading(false);
					toastText(
						'Unable to find account information. Please try again.',
						'error'
					);
					return;
				}
			} else {
				console.error('No subscription found');
				setIsLoading(false);
				toastText(
					'Unable to find subscription information. Please try again.',
					'error'
				);
				return;
			}

			const disconnectPayload: any = {
				companyId: companyId,
				outSetaToken: localStorage.getItem('outsetaAccessToken'),
				isNewOutsetaPlan: isNewOutsetaPlan,
				outSetaAccountUid: outSetaAccountUid,
			};

			postApi('/quickbooks/disconnect', disconnectPayload)
				.then(() => {
					toastText('QuickBooks disconnected successfully', 'success');
					return deleteApi(`/companies/company/${companyId}`);
				})
				.then(() => {
					setIsModalOpen(false);
					setModalVisible(false);
					setModalAction('');

					dispatch(
						updateCompanyConnect({
							companyId: companyId,
							value: false,
						})
					);
				})
				.then(() => {
					const params = {
						data: {
							key: companyId,
						},
						value: false,
					};

					dispatch(updateCompanyStatus(params));
				})
				.then(() => {
					setSelectedSidebar('Integrations');
					toastText(
						'Company deletion has started. You may continue working with another available company.',
						'success',
						10000
					);
					setIsLoading(false);

					closeDrawerByAnimation(false);
				})
				.catch((err: any) => {
					setIsModalOpen(false);
					setModalVisible(false);
					setModalAction('');
					setIsLoading(false);
					toastText(err?.response?.data?.message, 'error');
					window.location.reload();
				});
		} else {
			// Reconnect flow
			setIsLoading(true);
			const company = companiesData?.find(
				(c: any) => c?.companyId === companyId || c?.company?.id === companyId
			);

			const subscriptions = company?.company?.Subscription;
			const subscription =
				subscriptions && Array.isArray(subscriptions) ? subscriptions[0] : null;
			const newPlanUid = process.env.REACT_APP_OUTSETA_PLAN_UID_2;

			if (!newPlanUid) {
				console.error(
					'REACT_APP_OUTSETA_PLAN_UID_2 is not set in environment variables'
				);
				setIsLoading(false);
				toastText('Something went wrong', 'error');
				return;
			}

			let isNewOutsetaPlan = false;
			let outSetaAccountUid: string | null = null;

			if (subscription && newPlanUid) {
				const zohoProductId =
					subscription?.zohoProductId ||
					subscription?.zohoSubscriptionPlan?.Plan?.Uid;

				if (zohoProductId && zohoProductId === newPlanUid) {
					isNewOutsetaPlan = true;
				} else {
					isNewOutsetaPlan = false;
				}

				outSetaAccountUid =
					subscription?.zohoSubscriptionPlan?.Account?.Uid || null;

				if (!outSetaAccountUid) {
					console.error('No account UID found for reconnect');
					setIsLoading(false);
					toastText('Something went wrong', 'error');
					return;
				}
			} else {
				console.error('No subscription found for reconnect');
				setIsLoading(false);
				toastText('Something went wrong', 'error');
				return;
			}

			const requiresReauthorization =
				company?.company?.requiresReauthorization === true;

			localStorage.setItem('isConnectionExisting', 'true');
			localStorage.setItem('companyId', companyId);
			localStorage.setItem(
				'isNewOutsetaPlan',
				isNewOutsetaPlan ? 'true' : 'false'
			);
			localStorage.setItem('outsetaAccountUidForConnection', outSetaAccountUid);

			if (requiresReauthorization) {
				localStorage.setItem('transferSubscription', 'true');
			}

			localStorage.removeItem('payPeriod');
			dispatch(getCompanyDetails(companyId));
			setModalVisible(false);
			getApi('/quickbooks/authurl')
				.then((res) => {
					setIsLoading(false);
					localStorage.setItem('qbUrl', res.data.data);
					window.open(res.data.data, '_self');
				})
				.catch(() => {
					setIsLoading(false);
					toastText('Something went wrong', 'error');
				});
		}
	};

	const disconnectModalConfirmation = () => {
		if (modalAction === 'Cancel Subscription') {
			setIsModalOpen(false);
			setModalVisible(true);
		} else {
			submitHandler();
		}
	};

	const handleCloseModal = () => {
		setModalVisible(false);
		setModalAction('');
	};

	// Public triggers (data = the mapped row; data.key = companyId)
	const requestDisconnect = (data: any) => showModal(true, data, 'Disconnect');
	const requestCancel = (data: any) =>
		showModal(true, data, 'Cancel Subscription');
	const requestReconnect = (data: any) => showConnectModal(false, data);

	const modals = (
		<>
			{isModalOpen && (
				<DisconnectModal
					isModalOpen={isModalOpen}
					handleOk={disconnectModalConfirmation}
					handleCancel={hideModal}
					text={`Are you sure, you want to ${modalAction}?`}
					buttonText="Yes"
					buttonColor="disconnect"
					image="assets/images/disconnect-image.png"
					isLoading={isLoading}
				/>
			)}

			{isConnectModalOpen && (
				<DisconnectModal
					isModalOpen={isConnectModalOpen}
					handleOk={submitHandler}
					handleCancel={hideConnectModal}
					text="Are you sure, you want to Reconnect?"
					buttonText="Yes"
					buttonColor="connect"
					image="assets/images/quickbooks.png"
					isLoading={isLoading}
					isReconnect={true}
				/>
			)}

			{isModalVisible && (
				<WarningModal
					visible={isModalVisible}
					onCancel={handleCloseModal}
					onConfirm={submitHandler}
					isLoading={isLoading}
				/>
			)}
		</>
	);

	return {
		requestDisconnect,
		requestCancel,
		requestReconnect,
		modals,
		isLoading,
	};
};
